// Authentication store using Zustand for KATA Dashboard

import { create } from 'zustand';
import { AuthState, LoginCredentials, User, UserProfileUpdate, AuthTokens, AccessContext } from '../types/auth';
import { AuthService } from '../services/auth';
import { AccessService } from '../services/access';
import { setAuthTokens, getAuthTokens, setCachedUser } from '../services/api';

interface AuthActions {
  // Authentication actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (updates: UserProfileUpdate) => Promise<void>;
  loadAccessContext: (force?: boolean) => Promise<AccessContext | null>;
  
  // State management
  setUser: (user: User | null) => void;
  setTokens: (tokens: AuthTokens | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  
  // Initialization
  initializeAuth: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

// React StrictMode and several layout consumers can request the context at the
// same time. Keep one shared request so a failing endpoint is not hammered by
// duplicate calls and retries.
let accessContextRequest: Promise<AccessContext | null> | null = null;
let accessContextRequestUserId: number | null = null;
let accessContextRequestGeneration = 0;

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial state
  user: null,
  tokens: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  accessContext: null,
  accessContextStatus: 'idle',
  accessContextError: null,

  // Set user and update authentication state
  setUser: (user: User | null) => {
    const currentUserId = get().user?.id;
    const userChanged = currentUserId !== user?.id;
    set({
      user,
      isAuthenticated: !!user,
      ...(!user || userChanged ? {
        accessContext: null,
        accessContextStatus: user ? 'idle' : 'idle',
        accessContextError: null,
      } : {}),
    });
  },

  loadAccessContext: async (force = false) => {
    if (!get().isAuthenticated) {
      set({
        accessContext: null,
        accessContextStatus: 'idle',
        accessContextError: null,
      });
      return null;
    }

    const requestedUserId = get().user?.id;
    if (accessContextRequest && accessContextRequestUserId === requestedUserId) {
      return accessContextRequest;
    }
    if (!force && get().accessContextStatus === 'ready') {
      return get().accessContext;
    }

    set({ accessContextStatus: 'loading', accessContextError: null });
    const requestGeneration = ++accessContextRequestGeneration;
    const requestPromise: Promise<AccessContext | null> = (async () => {
      try {
        const context = await AccessService.getContext();
        if (get().user?.id !== requestedUserId) return null;
        set({
          accessContext: context,
          accessContextStatus: 'ready',
          accessContextError: null,
        });
        return context;
      } catch (error: any) {
        const message = error?.message || 'Failed to load plant access.';
        console.warn('Failed to load access context:', message);
        if (get().user?.id !== requestedUserId) return null;
        set({
          accessContext: null,
          accessContextStatus: 'error',
          accessContextError: message,
        });
        return null;
      } finally {
        if (accessContextRequestGeneration === requestGeneration) {
          accessContextRequest = null;
          accessContextRequestUserId = null;
        }
      }
    })();
    accessContextRequest = requestPromise;
    accessContextRequestUserId = requestedUserId ?? null;
    return requestPromise;
  },

  // Set tokens and update API client
  setTokens: (tokens: AuthTokens | null) => {
    setAuthTokens(tokens);
    set({ tokens });
  },

  // Set loading state
  setLoading: (isLoading: boolean) => {
    set({ isLoading });
  },

  // Set error message
  setError: (error: string | null) => {
    set({ error });
  },

  // Clear error message
  clearError: () => {
    set({ error: null });
  },

  // Login user
  login: async (credentials: LoginCredentials) => {
    const { setUser, setTokens, setLoading, setError, clearError } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const response = await AuthService.login(credentials);
      
      // Set tokens first (this updates the API client)
      const tokens = {
        access: response.access,
        refresh: response.refresh,
      };
      setTokens(tokens);
      
      // Set user and cache it
      setUser(response.user);
      
      // Cache user data for future page loads
      setCachedUser(response.user);
      await get().loadAccessContext();
      
    } catch (error: any) {
      setError(error.message || 'Login failed');
      setTokens(null);
      setUser(null);
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // Logout user
  logout: async () => {
    const { tokens, setUser, setTokens, setLoading, setError } = get();
    
    try {
      setLoading(true);
      
      if (tokens?.refresh) {
        await AuthService.logout(tokens.refresh);
      }
    } catch (error: any) {
      // Log error but don't throw - we want to clear local state regardless
      console.warn('Logout error:', error.message);
    } finally {
      // Clear local state
      setTokens(null);
      setUser(null);
      // Plant selections are authorization-scoped; never carry one account's
      // selection into the next account using the same browser.
      localStorage.removeItem('kata_plant_filter');
      setError(null);
      setLoading(false);
    }
  },

  // Refresh user data
  refreshUser: async () => {
    const { setUser, setLoading, setError, isAuthenticated } = get();
    
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      const user = await AuthService.getCurrentUser();
      setUser(user);
    } catch (error: any) {
      setError(error.message || 'Failed to refresh user data');
      // Don't logout on user refresh failure - token might still be valid
    } finally {
      setLoading(false);
    }
  },

  // Update user profile
  updateProfile: async (updates: UserProfileUpdate) => {
    const { setUser, setLoading, setError, clearError } = get();
    
    try {
      setLoading(true);
      clearError();
      
      const updatedUser = await AuthService.updateProfile(updates);
      setUser(updatedUser);
      
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // Initialize authentication on app startup (OPTIMIZED - NO API CALLS)
  initializeAuth: async () => {
    const { setUser, setTokens, setLoading, setError } = get();
    
    try {
      setLoading(true);
      
      // Check for stored tokens
      const storedTokens = getAuthTokens();
      if (!storedTokens) {
        return; // No tokens, user is not authenticated
      }
      
      // Set tokens immediately (no verification needed - let API calls handle expired tokens)
      setTokens(storedTokens);
      
      // Try to get cached user data first
      const cachedUser = AuthService.getCachedCurrentUser();
      if (cachedUser) {
        // Use cached user data - instant authentication!
        setUser(cachedUser);
        await get().loadAccessContext();
        return;
      }
      
      // No cached user, fetch from API (only if no cache)
      try {
        const user = await AuthService.getCurrentUser();
        setUser(user);
        await get().loadAccessContext();
      } catch (error: any) {
        // API call failed, but we still have tokens - let the user try to use the app
        // The API interceptor will handle token refresh if needed
        console.warn('Failed to fetch user data on init, will retry on first API call:', error.message);
      }
      
    } catch (error: any) {
      // If initialization fails, clear tokens and continue as unauthenticated
      console.warn('Auth initialization failed:', error.message);
      setTokens(null);
      setUser(null);
      setError(null); // Don't show error on initialization failure
    } finally {
      setLoading(false);
    }
  },
}));

// Utility hooks for common auth checks
export const useAuth = () => {
  const { user, isAuthenticated, isLoading } = useAuthStore();
  return { user, isAuthenticated, isLoading };
};

export const useIsAdmin = () => {
  const { user } = useAuthStore();
  return AuthService.isAdmin(user);
};

export const useUserDisplayName = () => {
  const { user } = useAuthStore();
  return AuthService.getUserDisplayName(user);
};
